import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import store from '@store';
import { urlSearchParams } from '@utility/utills';
import { flowType } from '@utility/userInfo';
import {
  pushModifierToItem,
  setNestedModifierArray,
  setTempModifier,
  setGlobalSelectedModifierId,
  clearGlobalModifiersData,
  setFunctionModifierId,
  getGlobalModifierData,
  toggleNestedModifierFlag,
} from '@reducers/orders';
import { GlobalModifierButtons } from './OrderItemHeader/OrderItemHeader';
import OrderItemHeader from './OrderItemHeader/OrderItemHeader';
import OrderItemDetailsMenu from './OrderItemDetailMenu';
import OrderItemModifiers from './OrderItemModifiers';
import OrderItemFooter from './OrderItemFooter';
import OrderItemModifierCart from './OrderItemModifierCart';
import LinkedItems from './OrderItemModifiers/LinkedItems';
import GlobalModifierPanel from './GlobalModifier/GlobalModifierPanel';
import ModFunctionBar from './OrderItemModifiers/ModFunctionBar';
// import ModFunctionBar from './OrderItemModifiers/ModFunctionBar';
import { ReactComponent as InfoLogo } from '@assets/images/info.svg';
import cn from 'classnames';
import { TouchableDiv } from '@components/common/Touchable';
import { ReactComponent as Searchimg } from '@assets/images/SearchIcon.svg';
import ModifierSearch from '@containers/OrderItemDetail/searchModifiersModal';
import { getPath, handleModifierClickFromSearch } from './OrderItemDetailHandler';
import style from '../MenuOrderV2/AppSidebarV2/AppSidebarV2.module.scss';

const getInitialNote = (internalId) => {
  const { cart } = store.getState().orders;
  if (internalId) {
    const cartItem = (cart || []).find(c => c.internalId === Number(internalId));
    if (cartItem?.note) return cartItem.note;
  }
  const parentMenuItemId = urlSearchParams('parentMenuItemId');
  if (parentMenuItemId) {
    const parentCartItem = (cart || []).find(
      c => String(c.menuItemId) === String(parentMenuItemId)
    );
    if (parentCartItem?.note) return parentCartItem.note;
    const tempNote = window.sessionStorage.getItem('pendingItemNote') || '';
    return tempNote;
  }
  return '';
};

const OrderItemDetail = ({
  isGlobalModActive,
  serverAppTheme,
  modifierGroupsHashTable,
  modifierLevelData,
  activeMenuItemId,
  auth,
  menuCard
}) => {
  const params = useParams();
  const { internalId, menuTypeId, menuItemId } = params;
  const { orders } = store.getState();
  const menuIndex = orders.menuIndex;
  const menusList = orders.menusList;
  const isNestedModifier = orders.isNestedModifier;
  const isGlobalMod =
    !isNestedModifier &&
    menusList[menuIndex]?.globalModifierGroupIds?.length !== 0;
  const modFunctionModifierGroupId =
    menusList?.[menuIndex]?.modFunctionModifierGroupId ?? null;

  const [note, setNoteState] = useState(() => getInitialNote(internalId));
  const [activeModGroupState, setSelectedModGroup] = useState({});
  const [info, setInfo] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [openKeyboard, setOpenKeyboard] = useState(false);

  const tablePopUp = useMemo(() => ({
    position: 'relative',
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    pointerEvents: 'auto',
    backgroundColor: serverAppTheme?.popUp_bg,
    backgroundClip: 'padding-box',
    height: '100%',
    border: serverAppTheme?.popupBorder,
    boxShadow: serverAppTheme?.popUp_box_shadow,
  }), [serverAppTheme]);


  const setNote = useCallback((value) => {
    setNoteState(value);
    window.sessionStorage.setItem('pendingItemNote', value);
  }, []);

  const isServerApp = useMemo(() => flowType() === 'serverApp', []);
  const globalModifierGroupIds = useMemo(
    () => menusList?.[menuIndex]?.globalModifierGroupIds ?? [],
    [menusList, menuIndex]
  );

  const handleGlobalModButtonClick = useCallback(
    modFunc => {
      store.dispatch(setGlobalSelectedModifierId(modFunc)).then(() => {
        store.dispatch(getGlobalModifierData(true));
      });
    },
    []
  );

  const itemInfo = useMemo(() => menuCard?.[menuTypeId]?.[menuItemId], [
    menuCard,
    menuTypeId,
    menuItemId,
  ]);
  const parentMenuItemInfo = useMemo(() => {
    const parentId = urlSearchParams('parentMenuItemId');
    return parentId
      ? menuCard?.[params.menuTypeId]?.[Number(parentId)]
      : null;
  }, [menuCard, params.menuTypeId]);

  const itemDetailsInfo = parentMenuItemInfo || itemInfo;

  // used to set nested activeModGroup selection
  useEffect(() => {
    setSelectedModGroup({});
  }, [modifierLevelData]);

  useEffect(() => {
    if (params.internalId) {
      const { cart } = store.getState().orders;
      const cartItem = cart.find(
        c => c.internalId === Number(params.internalId)
      );
      if (cartItem?.modifierData) {
        store.dispatch(pushModifierToItem(cartItem.modifierData));
      }
    } else {
      // Replicate V1 renderInitialData(): pre-populate modifierData with auto-add
      // entries (hiddenAutoAddMenuItemId / hiddenAutoAddModifierId) from each
      // modifier group link. This is how linked items get pre-selected on load.
      const { orders } = store.getState();
      const {
        menuItemsHashTable,
        modifierGroupsHashTable,
        modifiersHashTable,
      } = orders;
      const activeMenuItemId = Number(urlSearchParams('menuId')) || 0;
      const modifierGroupLinks =
        menuItemsHashTable[params.menuItemId]?.modifierGroupLinks || [];
      const modifierList = modifierGroupLinks.map(item => ({
        ...item,
        ...modifierGroupsHashTable[item.modifierGroupId],
      }));
      const preSelectModList = [];
      modifierList.forEach(modGroup => {
        if (
          modGroup.hiddenMenuId === activeMenuItemId &&
          ((modGroup.hiddenAutoAddModifierId !== null && !modGroup.hiddenMenuId) ||
            (modGroup.hiddenAutoAddMenuItemId !== null && !modGroup.hiddenMenuId))
        ) {
          preSelectModList.push(
            modGroup.hiddenAutoAddModifierId
              ? `${modGroup.hiddenAutoAddModifierId}-${modGroup.id}`
              : `${modGroup.hiddenAutoAddMenuItemId}-${modGroup.id}`
          );
        } else if (modGroup.hiddenAutoAddMenuItemId) {
          preSelectModList.push(
            `${modGroup.hiddenAutoAddMenuItemId}-${modGroup.id}`
          );
        } else if (
          modGroup.hiddenAutoAddModifierId &&
          modifiersHashTable[modGroup.hiddenAutoAddModifierId]
        ) {
          preSelectModList.push(
            `${modGroup.hiddenAutoAddModifierId}-${modGroup.id}`
          );
        }
      });
      store.dispatch(pushModifierToItem(preSelectModList));
      store.dispatch(setNestedModifierArray([]));
      store.dispatch(setTempModifier([]));
    }

    store.dispatch(toggleNestedModifierFlag(false));
    store.dispatch(setGlobalSelectedModifierId(null));
    store.dispatch(clearGlobalModifiersData());
    store.dispatch(setFunctionModifierId(null));
  }, [params.menuItemId, params.menuTypeId, params.internalId]);

  /** Show global mod pill buttons only in serverApp, when not nested */
  const showGlobalModButtons = useMemo(
    () =>
      isServerApp &&
      !isNestedModifier &&
      globalModifierGroupIds.length > 0,
    [isServerApp, isNestedModifier, globalModifierGroupIds]
  );

  return (
    <div className='h-100 pt-2'>
      <div className={cn('d-flex fs_18 fw-500 justify-content-between align-items-center px-2 py-1')} style={{ color: serverAppTheme?.btn_font_color, borderRadius: '4px', height: '42px', background: '#74A4FF', margin: '0 12px' }}>
        <div className="col-9 px-0 text-truncate">{itemDetailsInfo?.menuItem?.name}</div>
        <div className="col-3 pr-0 d-flex align-items-center justify-content-end">
          {auth?.serverAppFeatures?.displayPrice && (
            <div>${itemDetailsInfo?.price.toFixed(2)}</div>
          )}

          <div className="pl-2">
            <TouchableDiv
              className="d-flex align-items-center justify-content-center h-90 rounded px-0"
              style={{
                backgroundColor: serverAppTheme?.global_mod_tile,
                height: '29px',
                width: '29px',
              }}
              role="presentation"
              onClick={e => {
                e.preventDefault();
                setInfo(!info);
              }}
              rippleEffect={true}
            >
              <InfoLogo
                fill={serverAppTheme?.iconcolor}
                alt="help"
                className="w-100"
              />
            </TouchableDiv>
          </div>

          <div className="pl-2">
            <TouchableDiv
              role="button"
              tabIndex="0"
              className={cn(style.searchdiv, 'px-3')}
              onClick={e => {
                e.preventDefault();
                setOpenKeyboard(true);
                setSearchModalOpen(true);
              }}
              style={{ background: serverAppTheme?.tip_tile }}
            >
              <Searchimg stroke={serverAppTheme?.notification_time_font} />
              <span
                style={{
                  '--placeholder-color': serverAppTheme?.notification_time_font,
                }}
                className={cn(style.searchStyle, 'pr-2')}
              >
                Search
              </span>
            </TouchableDiv>
          </div>

        </div>
      </div>
      <div className="d-flex w-100 p-12 h-92">
        <div
          className="d-flex col-9 flex-column pl-0"
          style={{ height: '100%', paddingRight: '12px' }}
        >
          {/* Header: normal item info + global mod buttons, OR search + alphabet when global mod active */}
          <div
            className="d-flex w-100"
            style={{ background: 'rgb(245, 247, 251)' }}
          >
            {/* mod function buttons */}
            <div className="w-100">
              {!isGlobalModActive && <ModFunctionBar />}

              {/* Global modifier pill buttons */}
              {showGlobalModButtons && !isGlobalModActive && (
                <GlobalModifierButtons
                  globalModifierGroupIds={globalModifierGroupIds}
                  modifierGroupsHashTable={modifierGroupsHashTable}
                  serverAppTheme={serverAppTheme}
                  onButtonClick={handleGlobalModButtonClick}
                />
              )}
            </div>
          </div>
          {/* <ModFunctionBar /> */}
          {/* <div className="d-flex px-2 w-100 pt-3"> */}
          <LinkedItems />
          {/* </div> */}
          {/* <div className="d-flex px-2 w-100">
          {!isGlobalModActive && <OrderItemDetailsMenu activeModGroup={activeModGroup}
            setSelectedModGroup={setSelectedModGroup}/>}
        </div> */}

          {/* Modifier area: global modifier panel replaces normal modifiers when active */}
          <div
            className="px-0 w-100 mb-2"
            style={{
              flex: '1 1 0',
              minHeight: 0,
              border: '1px solid #DADADA',
              background: '#FFF',
              borderRadius: '4px',
            }}
          >
            <OrderItemHeader info={info} setInfo={setInfo} />

            <div className="d-flex px-0 w-100">
              {!isGlobalModActive && (
                <OrderItemDetailsMenu
                  activeModGroup={activeModGroupState}
                  setSelectedModGroup={setSelectedModGroup}
                  serverAppTheme={serverAppTheme}
                  modifierGroupsHashTable={modifierGroupsHashTable}
                />
              )}
            </div>
            {isGlobalModActive ? (
              <GlobalModifierPanel />
            ) : (
              <OrderItemModifiers
                activeModGroup={activeModGroupState}
                showGlobalModButtons={showGlobalModButtons}
                modFunctionModifierGroupId={modFunctionModifierGroupId}
              />
            )}
          </div>

          <div className="d-flex h-9 p-2 w-100"
        style={{background: serverAppTheme?.card_bg,
              height: '60px',
            }}>
            <OrderItemFooter note={note} onNoteChange={setNote} onResetModGroupFilter={() => setSelectedModGroup({})} />
          </div>
        </div>

        <div className="d-flex col-3 h-100 px-0">
          <OrderItemModifierCart
            note={note}
          />
        </div>
      </div>

      {searchModalOpen && (
        <ModifierSearch
          isOpen={searchModalOpen}
          serverAppTheme={serverAppTheme}
          customClass2={cn('w-100 h-100')}
          customClass=""
          placeholderName="Search items"
          setSearchModalOpen={setSearchModalOpen}
          setOpenKeyboard={setOpenKeyboard}
          openKeyboard={openKeyboard}
          style={tablePopUp}
          modifierGroupsHashTable={modifierGroupsHashTable}
          menuItemId={menuItemId}
          modifierLevelData={modifierLevelData}
          getPath={getPath}
          onModifierClickFromSearch={(data) => handleModifierClickFromSearch(data, setSearchModalOpen)}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ orders, theme, oauth }) => ({
  isGlobalModActive: Boolean(orders.globalSelectedModifierId),
  serverAppTheme: theme.serverAppTheme,
  modifierGroupsHashTable: orders.modifierGroupsHashTable,
  modifierLevelData: orders.modifierLevelData,
  activeMenuItemId: Number(urlSearchParams('menuId')) || 0,
  auth: oauth.authInfo,
  menuCard: orders.menuCardList,
});

export default connect(mapStateToProps)(React.memo(OrderItemDetail));
